import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { useTranslation } from 'react-i18next';
import { rtuReachabilityService } from '../../services/rtuReachabilityService';
import { RtuReachabilityResponse } from '../../types/RtuReachability';
import { RtuSupportedRange } from '../../types/RtuStatus';
import { useMetricCardCompact } from '../../hooks/useMetricCardCompact';
import { usePrimeChartPanelResize } from '../../hooks/usePrimeChartPanelResize';
import {
  formatCheckSuccessRatePercent,
  REACHABILITY_INFO_TOOLTIP_DEFAULT,
} from '../../utils/reachabilityDisplay';
import { SkeletonChartPanel, SkeletonRegion } from '../common/Skeleton/Skeleton';

const RANGE_PRESETS: Array<{ range: RtuSupportedRange; chipLabel: string }> = [
  { range: '24h', chipLabel: '24H' },
  { range: '7d', chipLabel: '7D' },
  { range: '30d', chipLabel: '30D' },
];

const formatUtc = (value: string | null | undefined) => {
  if (!value) {
    return '\u2014';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatPointLabel = (value: string, range: RtuSupportedRange) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  if (range === '24h') {
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const ReachabilityCard = () => {
  const { t } = useTranslation();
  const [range, setRange] = useState<RtuSupportedRange>('24h');
  const [data, setData] = useState<RtuReachabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasDataRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    hasDataRef.current = data !== null;
  }, [data]);

  const loadData = useCallback(
    async (nextRange: RtuSupportedRange) => {
      const requestId = ++requestIdRef.current;
      if (hasDataRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await rtuReachabilityService.fetch({ range: nextRange });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setData(response);
        setError(null);
      } catch (serviceError: any) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(
          serviceError?.response?.data?.message ||
            serviceError?.message ||
            t('dashboard.reachability.errors.loadFailed', {
              defaultValue: 'Unable to load fleet reachability.',
            })
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    // See the matching comment in StatusHistoryChart.tsx / src/locales/i18n.ts:
    // `t` here is only used for a failure-message string, not to decide what
    // to fetch - keeping it out of the dependency list stops this card from
    // refetching on every unrelated Redux/i18next re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    void loadData(range);
  }, [loadData, range]);

  const summary = data?.summary ?? {
    successCount: 0,
    failureCount: 0,
    totalChecks: 0,
    successRatePercent: 0,
    latestSuccessRatePercent: 0,
  };

  const chartData = useMemo(() => {
    const points = data?.points ?? [];

    return {
      labels: points.map((point) => formatPointLabel(point.bucketStartUtc, range)),
      datasets: [
        {
          label: t('dashboard.reachability.series.successRate', { defaultValue: 'Success rate' }),
          data: points.map((point) => point.successRatePercent),
          rawCounts: points.map((point) => point.successCount),
          borderColor: '#2b8a3e',
          backgroundColor: 'rgba(43, 138, 62, 0.65)',
          borderWidth: 1,
          borderRadius: 3,
          stack: 'reachability',
        },
        {
          label: t('dashboard.reachability.series.failureRate', {
            defaultValue: 'Not fully successful checks',
          }),
          data: points.map((point) => Math.max(0, 100 - point.successRatePercent)),
          rawCounts: points.map((point) => point.failureCount),
          borderColor: '#6f7b85',
          backgroundColor: 'rgba(111, 123, 133, 0.68)',
          borderWidth: 1,
          borderRadius: 3,
          stack: 'reachability',
        },
      ],
    };
  }, [data, range, t]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      categoryPercentage: 0.7,
      barPercentage: 0.85,
      plugins: {
        legend: {
          display: true,
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            color: '#20313f',
            font: { size: 11 },
          },
        },
        tooltip: {
          callbacks: {
            title: (tooltipItems: Array<{ dataIndex: number }>) => {
              const bucketStartUtc = data?.points?.[tooltipItems[0]?.dataIndex]?.bucketStartUtc;
              return formatUtc(bucketStartUtc);
            },
            label: (context: any) => {
              const dataset = context.dataset as { label?: string; rawCounts?: number[] };
              const raw = dataset.rawCounts?.[context.dataIndex] ?? 0;
              const value = Number(context.parsed?.y ?? 0).toFixed(1);
              return `${dataset.label ?? ''}: ${raw} (${value}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: '#5d6f7e',
            autoSkip: true,
            maxTicksLimit: 8,
            maxRotation: 0,
            font: { size: 11 },
          },
          grid: { color: 'rgba(32, 49, 63, 0.06)' },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          ticks: {
            color: '#5d6f7e',
            stepSize: 20,
            callback: (value: number) => `${value}%`,
            font: { size: 11 },
          },
          grid: { color: 'rgba(32, 49, 63, 0.08)' },
        },
      },
    }),
    [data?.points]
  );

  const { ref: metricBodyRef, compact: metricCompact } = useMetricCardCompact();
  const chartPanelRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart>(null);
  const reachLayoutKey = `${range}:${data?.generatedAtUtc ?? ''}:${data?.points?.length ?? 0}`;
  const showReachChart = Boolean(data && data.points.length > 0);
  usePrimeChartPanelResize(chartPanelRef, chartRef, showReachChart, reachLayoutKey);

  return (
    <div className="dashboard-reachability">
      <div className="dashboard-reachability__header">
        <div className="dashboard-reachability__chip-group">
          {RANGE_PRESETS.map((preset) => (
            <button
              key={preset.range}
              type="button"
              className={`dashboard-reachability__chip${range === preset.range ? ' is-active' : ''}`}
              onClick={() => setRange(preset.range)}
            >
              {preset.chipLabel}
            </button>
          ))}
          <Tooltip
            title={t('dashboard.reachability.infoTooltip', {
              defaultValue: REACHABILITY_INFO_TOOLTIP_DEFAULT,
            })}
            arrow
          >
            <InfoOutlinedIcon
              data-testid="reachability-info-tooltip"
              fontSize="small"
              color="action"
              aria-label={t('dashboard.reachability.infoTooltipAria', {
                defaultValue: 'Reachability explanation',
              })}
              sx={{ cursor: 'help', fontSize: '1rem', alignSelf: 'center' }}
            />
          </Tooltip>
        </div>
        <div className="dashboard-reachability__meta">
          {refreshing ? <span>{t('dashboard.history.refreshing')}</span> : null}
          <span>
            {t('dashboard.reachability.updatedAt', {
              value: formatUtc(data?.generatedAtUtc),
              defaultValue: 'Updated: {{value}}',
            })}
          </span>
        </div>
      </div>

      <div
        ref={metricBodyRef}
        className={`dashboard-reachability__body${metricCompact ? ' dashboard-metric-card--compact' : ''}`}
      >
        <aside
          className="dashboard-history-chart__summary-rail"
          data-testid="reachability-kpi-rail"
          aria-label={t('dashboard.reachability.kpiRail', { defaultValue: 'Reachability summary' })}
        >
          <div className="dashboard-history-chart__summary-row">
            <div className="dashboard-history-chart__summary-top">
              <span>
                {t('dashboard.reachability.kpi.successRate', { defaultValue: 'Check Success Rate' })}
              </span>
              <span data-testid="reachability-success-rate">
                {formatCheckSuccessRatePercent(summary.successRatePercent)}
              </span>
            </div>
            <div className="dashboard-history-chart__summary-bar" aria-hidden="true" />
          </div>
          <div className="dashboard-history-chart__summary-row">
            <div className="dashboard-history-chart__summary-top">
              <span>
                {t('dashboard.reachability.kpi.totalChecks', { defaultValue: 'Checks Performed' })}
              </span>
              <span data-testid="reachability-total-checks">{summary.totalChecks}</span>
            </div>
            <div className="dashboard-history-chart__summary-bar" aria-hidden="true" />
          </div>
        </aside>

        <div ref={chartPanelRef} className="dashboard-reachability__chart-panel">
          {loading && !data ? (
            <SkeletonRegion label={t('dashboard.reachability.loading', { defaultValue: 'Loading fleet reachability...' })}>
              <SkeletonChartPanel />
            </SkeletonRegion>
          ) : null}

          {!loading && error && !data ? (
            <div className="dashboard-reachability__state" role="alert">
              <div>{error}</div>
              <Button
                label={t('dashboard.history.retry')}
                className="p-button-sm p-button-outlined"
                onClick={() => void loadData(range)}
              />
            </div>
          ) : null}

          {data && data.points.length > 0 ? (
            <div className="dashboard-reachability__canvas">
              <Chart ref={chartRef} type="bar" data={chartData} options={chartOptions} />
            </div>
          ) : null}

          {data && data.points.length === 0 && !loading ? (
            <div className="dashboard-reachability__state" role="status">
              {t('dashboard.reachability.empty', { defaultValue: 'No reachability points are available for the selected period.' })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReachabilityCard;
